import { connect } from "react-redux";

import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { withTestPageObjectRegistration } from "../../Services/TestPageObjects/PageObjectExposure";
import { CandyDispatch, CandyThunkAction } from "../../../Engine/Core/Store/CandyReduxTypes";
import {
    addInstance,
    LastInstanceBehaviour,
    removeInstance,
    revalidateChildrenPaths,
} from "../../../Engine/ActionCreators/AllActionCreators";
import { withDeferredInit } from "../../withDeferredInit";
import { schedulerWrapper, TaskPriority } from "../../../Helpers/Scheduler/SchedulerApi";
import { FormActionHandlerExecutionInjectableProps } from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";

import { Tabs } from "./Tabs";
import { TabInfosActionTypes } from "./Store/TabInfosActions";

interface TabsContainerProps extends TabTitleProps {
    bindingPath: AbsoluteResolvedModelPath;
    viewModelSelect: ViewModelSelect;
    deferredInit?: boolean;
}

interface TabTitleProps extends FormActionHandlerExecutionInjectableProps {
    onClose?: () => void;
    children: React.ReactNode;
}

export function calculateCurrentTabIndex(path: AbsoluteModelPath, lastToken: string): CandyThunkAction<string> {
    return (_, __, form) => {
        const instances = form.getChildren(path.toLegacyPath()) ?? [];
        const token = instances.indexOf(lastToken);
        return token === -1 ? "0" : token.toString();
    };
}

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: TabsContainerProps) => ({
    onAddInstance: () => {
        dispatch(addInstance(ownProps.bindingPath));
    },
    onRemoveInstance: (bindingPath: AbsoluteResolvedModelPath, instancesToRemove: string) => {
        dispatch(removeInstance(bindingPath, instancesToRemove, LastInstanceBehaviour.Clear));
    },
    revalidateChildrenPaths: async (path: AbsoluteModelPath) => await dispatch(revalidateChildrenPaths(path)),
    calculateCurrentTabIndex: (path: AbsoluteModelPath, lastToken: string) =>
        dispatch(calculateCurrentTabIndex(path, lastToken)),
    setCurrentTabIndex: (instance: string | undefined) => {
        schedulerWrapper(
            () => {
                dispatch({
                    type: TabInfosActionTypes.SetCurrentTab,
                    payload: {
                        instance: instance,
                        bindingPath: ownProps.bindingPath.toLegacyPath(),
                    },
                });
            },
            { priority: TaskPriority.Background }
        );
    },
    setCurrentTab: (instance: string | undefined) => {
        schedulerWrapper(
            () => {
                dispatch({
                    type: TabInfosActionTypes.ChangeTab,
                    payload: {
                        instance: instance,
                        bindingPath: ownProps.bindingPath.toLegacyPath(),
                    },
                });
            },
            { priority: TaskPriority.UserVisible }
        );
    },
});

const mapStateToProps = (state: FullFormState, { bindingPath, viewModelSelect }: TabsContainerProps) => {
    const children = viewModelSelect.children(state, bindingPath, []);
    return {
        instances: children,
        childrenCount: children.length,
        currentTab: state.tabInfos.currentInstance[bindingPath.toLegacyPath()],
    };
};

export const TabsContainer = withResolvePaths(
    withViewModelSelect(
        withFocusManagement(
            connect(
                mapStateToProps,
                mapDispatchToProps
            )(withDeferredInit(withControlDebugInfo(withTestPageObjectRegistration(Tabs))))
        )
    )
);
